import { HomeEvalOutcomeCells, HomeEvalOutcomeHeads } from '@/components/homeEval/HomeEvalOutcomeCells';
import { useRowClick } from '@/lib/navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { homeEvalResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { ConsiderationType } from '@rock/types';
import type { FC } from 'react';
import { CreateHomeEvalJobButton } from './CreateHomeEvalJobButton';

export const HomeEvalResultsTable: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const typeId = considerationType.id;
    const [results, resultsLoadable] = useLoadableAtomValue(homeEvalResultsState(typeId), []);
    const onRowClick = useRowClick();

    if (resultsLoadable.state === 'loading') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-muted-foreground">Loading home eval results…</div>
                </CardContent>
            </Card>
        );
    }

    if (resultsLoadable.state === 'hasError') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="text-destructive">
                        Error loading home eval results:{' '}
                        {resultsLoadable.error instanceof Error
                            ? resultsLoadable.error.message
                            : `${resultsLoadable.error}`}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="border-b-1 pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-xl">Home Eval Results</CardTitle>
                <CreateHomeEvalJobButton />
            </CardHeader>
            <CardContent>
                {results.length === 0 ? (
                    <div className="text-muted-foreground">No home eval results found for this consideration type.</div>
                ) : (
                    <div className="px-4 pb-4">
                        <Table className="not-prose">
                            <TableHeader className="bg-secondary">
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Home ID</TableHead>
                                    <HomeEvalOutcomeHeads />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {results.map((result) => (
                                    <TableRow
                                        key={result.id}
                                        onClick={onRowClick(`./${result.id}`)}
                                        className="cursor-pointer"
                                    >
                                        <TableCell>{new Date(result.createdAt).toLocaleString()}</TableCell>
                                        <TableCell className="font-mono text-xs">{result.homeId}</TableCell>
                                        <HomeEvalOutcomeCells result={result} />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
